/// <reference types="vite/client" />
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  Link,
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from 'react-oidc-context'
import { Toaster } from 'sonner'
import { TooltipProvider } from '~/components/ui/tooltip'
import { AppSidebar } from '~/components/AppSidebar'
import { DefaultCatchBoundary } from '~/components/DefaultCatchBoundary'
import { NotFound } from '~/components/NotFound'
import { authority, clientId } from '~/lib/auth'
import { captureReturnTo, sanitizeReturnTo } from '~/lib/return-to'
import { CurrentUserProvider, useCurrentUser } from '~/lib/current-user'
import {
  IdentityConflictDialog,
  isIdentityConflictError,
} from '~/components/IdentityConflictDialog'
import { isTestModeEnabled, TestModeAuthShim } from '~/lib/e2e-test-mode'
import {
  clearRecoveryAttempts,
  recordRecoveryAttempt,
  setAuthRecoveryHandler,
} from '~/lib/auth-recovery'
import { useSilentRenewStormGuard } from '~/lib/use-silent-renew-storm-guard'
import { WebStorageStateStore } from 'oidc-client-ts'
import { ThemeProvider } from '~/lib/theme-context'
import { Button } from '~/components/ui/button'
import appCss from '~/styles.css?url'

const APP_NAME = 'First Response'

/**
 * Routes that render without requiring an OIDC session. Additions must be
 * deliberate — the page and anything it transitively renders must be safe to
 * expose publicly. Currently empty: /openapi and /mcp-instructions used to be
 * public, but they're now gated by the api:read / mcp:read permissions, which
 * requires an authenticated session to evaluate.
 */
const PUBLIC_ROUTES = new Set<string>([])

// Module-level singleton: safe only while every cached query is workspace-wide
// and non-user-specific. User-scoped queries would leak across concurrent SSR
// requests — create a per-request QueryClient via route context if needed.
const queryClient = new QueryClient()

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: APP_NAME },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', href: '/favicon.svg' },
    ],
  }),
  errorComponent: DefaultCatchBoundary,
  notFoundComponent: NotFound,
  component: RootComponent,
})

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        <Scripts />
      </body>
    </html>
  )
}

function RootComponent() {
  const router = useRouter()
  const [testModeActive, setTestModeActive] = useState(false)

  // Test-mode detection runs AFTER hydration so SSR + first client render
  // always see the production tree (no hydration mismatch). In prod builds
  // `isTestModeEnabled()` is dead-code-eliminated to `false`.
  useEffect(() => {
    if (isTestModeEnabled()) setTestModeActive(true)
  }, [])

  if (testModeActive) {
    return (
      <RootDocument>
        <TestModeAuthShim>
          <TooltipProvider>
            <CurrentUserProvider>
              <AuthGate />
            </CurrentUserProvider>
            <Toaster richColors position="bottom-right" />
          </TooltipProvider>
        </TestModeAuthShim>
      </RootDocument>
    )
  }

  // Build OIDC config inside the component (client-side only — window available).
  const oidcConfig = import.meta.env.SSR
    ? { authority, client_id: clientId, redirect_uri: '', scope: 'openid profile email' }
    : {
        authority,
        client_id: clientId,
        redirect_uri: `${globalThis.location.origin}/callback`,
        post_logout_redirect_uri: globalThis.location.origin,
        scope: 'openid profile email',
        automaticSilentRenew: true,
        // Renew via the refresh_token grant instead of a hidden iframe — the
        // iframe flow relies on a cross-site session cookie that Safari ITP +
        // Chrome's third-party-cookie rules block.
        useRefreshToken: true,
        userStore: new WebStorageStateStore({ store: localStorage }),
      }

  return (
    <RootDocument>
      <AuthProvider
        {...oidcConfig}
        onSigninCallback={(user) => {
          const returnTo = (user?.state as { returnTo?: unknown } | undefined)?.returnTo
          globalThis.history.replaceState({}, document.title, sanitizeReturnTo(returnTo))
          router.invalidate()
        }}
      >
        <TooltipProvider>
          <ThemeProvider>
            <CurrentUserProvider>
              <AuthGate />
            </CurrentUserProvider>
            <Toaster richColors position="bottom-right" />
          </ThemeProvider>
        </TooltipProvider>
      </AuthProvider>
    </RootDocument>
  )
}

function AuthGate() {
  const auth = useAuth()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [recovering, setRecovering] = useState(false)
  const [recoveryExhausted, setRecoveryExhausted] = useState(false)
  const recoveringRef = useRef(false)
  const exhaustedRef = useRef(false)
  useEffect(() => {
    exhaustedRef.current = recoveryExhausted
  }, [recoveryExhausted])

  // Shared recovery flow: stop silent-renew, rate-limit the attempt, drop the
  // stale OIDC user, and bounce through Keycloak. See auth-recovery.ts.
  const runRecovery = useCallback(() => {
    if (recoveringRef.current || exhaustedRef.current) return
    if (PUBLIC_ROUTES.has(pathname) && !auth.isAuthenticated) return

    try { auth.stopSilentRenew() } catch { /* idempotent */ }

    let proceed = true
    try {
      const result = recordRecoveryAttempt({ storage: sessionStorage, now: Date.now() })
      proceed = result.shouldProceed
    } catch { /* storage disabled — fail open */ }

    if (!proceed) {
      setRecoveryExhausted(true)
      return
    }

    recoveringRef.current = true
    setRecovering(true)
    auth.removeUser().finally(() => {
      auth.signinRedirect({ state: { returnTo: captureReturnTo() } }).catch(() => {
        recoveringRef.current = false
        setRecovering(false)
      })
    })
  }, [auth, pathname])

  useEffect(() => {
    if (!auth.error) return
    console.error('[auth] OIDC error, triggering recovery:', auth.error)
    runRecovery()
  }, [auth.error, runRecovery])

  // RPC-layer 401s never set `auth.error`; rpcAuthMiddleware invokes the
  // handler we register here so those failures take the same recovery path.
  useEffect(() => {
    setAuthRecoveryHandler(runRecovery)
    return () => setAuthRecoveryHandler(null)
  }, [runRecovery])

  useSilentRenewStormGuard(auth)

  if (PUBLIC_ROUTES.has(pathname) && !auth.isAuthenticated) {
    return (
      <PublicShell onSignIn={() => auth.signinRedirect({ state: { returnTo: captureReturnTo() } })}>
        <Outlet />
      </PublicShell>
    )
  }

  if (auth.isLoading || recovering) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    )
  }

  if (auth.error || recoveryExhausted) {
    const message = auth.error?.message ?? 'Sign-in could not complete after multiple attempts.'
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-destructive text-sm">Authentication error: {message}</p>
        <Button
          onClick={() => {
            try { clearRecoveryAttempts(sessionStorage) } catch { /* ignore */ }
            setRecoveryExhausted(false)
            auth.signinRedirect({ state: { returnTo: captureReturnTo() } })
          }}
        >
          Try again
        </Button>
      </div>
    )
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold">{APP_NAME}</h1>
        <p className="text-muted-foreground">Sign in to continue</p>
        <Button onClick={() => auth.signinRedirect({ state: { returnTo: captureReturnTo() } })}>Sign in</Button>
      </div>
    )
  }

  return <RoleGate />
}

function PublicShell({
  children,
  onSignIn,
}: Readonly<{ children: ReactNode; onSignIn: () => void }>) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center justify-between border-b px-6 py-3">
        <Link to="/" className="text-lg font-semibold tracking-tight">
          {APP_NAME}
        </Link>
        <Button size="sm" onClick={onSignIn}>Sign in</Button>
      </header>
      <main className="flex-1">{children}</main>
    </div>
  )
}

function RoleGate() {
  const { user, permissions, loading, error } = useCurrentUser()
  // The seeded "None" role grants no permissions. Detect access-pending by an
  // empty permission set rather than role id, so a custom no-access role
  // behaves the same way.
  const hasNoAccess = permissions !== null && permissions.length === 0
  const auth = useAuth()

  if (loading || !user) {
    const identityConflict = isIdentityConflictError(error)
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground text-sm">
          {error && !identityConflict ? `Error: ${error.message}` : 'Setting up your account...'}
        </div>
        {identityConflict && (
          <IdentityConflictDialog
            email={auth.user?.profile.email}
            onSignOut={() => auth.signoutRedirect()}
          />
        )}
      </div>
    )
  }

  if (hasNoAccess) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-bold">Access pending</h1>
        <p className="text-muted-foreground max-w-md">
          Your account has been created but does not yet have permission to use
          this app. Please contact an administrator to have your access granted.
        </p>
        <p className="text-muted-foreground text-xs">Signed in as {user.email}</p>
        <Button variant="outline" onClick={() => auth.signoutRedirect()}>Sign out</Button>
      </div>
    )
  }

  return <AppLayout />
}

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === 'true'
    } catch {
      return false
    }
  })

  function handleCollapse(collapsed: boolean) {
    setSidebarCollapsed(collapsed)
    try { localStorage.setItem('sidebar-collapsed', String(collapsed)) } catch { /* ignore */ }
  }

  return (
    <div className="flex h-screen">
      <AppSidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onCollapse={handleCollapse}
      />
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center border-b px-4 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" x2="20" y1="12" y2="12" />
              <line x1="4" x2="20" y1="6" y2="6" />
              <line x1="4" x2="20" y1="18" y2="18" />
            </svg>
          </Button>
          <span className="ml-2 font-semibold">{APP_NAME}</span>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
